import { Fragment, useEffect, useState } from "react";
import type { BusinessListingApplicationRequest, BusinessListingMarketplaceItemResponse } from "../../types/businessListingTypes";
import { formatBusinessListingCompensation, formatBusinessListingDuration, formatBusinessListingJobType } from "../../utils/BusinessListingUtils";
import { formatCityStateZip, formatDistanceLabel, formatGigListLocation } from "../../utils/LocationUtils";
import Pagination from "../gigs/Pagination";
import type { DistanceFilter, PaySortOption } from "../gigs/TableFilters";
import ResponsiveDataList from "../ui/DataTable/ResponsiveDataList";
import TableContainer from "../ui/DataTable/TableContainer";
import TableHeader from "../ui/DataTable/TableHeader";
import type { DataTableColumn } from "../ui/DataTable/types";
import Modal from "../ui/Modal/Modal";
import BusinessListingApplyModal from "./BusinessListingApplyModal";
import BusinessListingDetailGrid, { type BusinessListingFeedback } from "./BusinessListingDetailGrid";

const columns: DataTableColumn[] = [
  { key: "title", label: "Title" },
  { key: "business", label: "Business" },
  { key: "location", label: "Location" },
  { key: "compensation", label: "Compensation" },
];

type BusinessListingsMarketplaceTableProps = {
  listings: BusinessListingMarketplaceItemResponse[];
  autoExpandListingId?: string | null;
  canApplyAsTeen: boolean;
  currentUserId?: string | null;
  currentUserZip?: string | null;
  isLoading: boolean;
  search: string;
  showFilters?: boolean;
  distance: DistanceFilter;
  paySort: PaySortOption;
  page: number;
  pageSize: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onDistanceChange: (value: DistanceFilter) => void;
  onPaySortChange: (value: PaySortOption) => void;
  onPageChange: (page: number) => void;
  onApply: (listingId: string, payload: BusinessListingApplicationRequest) => Promise<void>;
  onBeforeApply?: (listing: BusinessListingMarketplaceItemResponse) => boolean;
  emptyMessage?: string;
};

const isMobileBoardViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(max-width: 1023px)").matches;

const BusinessListingsMarketplaceTable = ({
  listings,
  autoExpandListingId,
  canApplyAsTeen,
  currentUserId,
  currentUserZip,
  isLoading,
  search,
  showFilters = true,
  distance,
  paySort,
  page,
  pageSize,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onDistanceChange,
  onPaySortChange,
  onPageChange,
  onApply,
  onBeforeApply,
  emptyMessage = "No business listings found.",
}: BusinessListingsMarketplaceTableProps) => {
  const [expandedListingId, setExpandedListingId] = useState<string | null>(null);
  const [applyModalListing, setApplyModalListing] = useState<BusinessListingMarketplaceItemResponse | null>(null);
  const [applyingListingId, setApplyingListingId] = useState<string | null>(null);
  const [feedbackByListingId, setFeedbackByListingId] = useState<Record<string, BusinessListingFeedback>>({});
  const [appliedListingIds, setAppliedListingIds] = useState<Record<string, true>>({});
  const [mobileDetailListingId, setMobileDetailListingId] = useState<string | null>(null);

  useEffect(() => {
    if (!autoExpandListingId) {
      return;
    }
    if (isMobileBoardViewport()) {
      setMobileDetailListingId(autoExpandListingId);
    } else {
      setExpandedListingId(autoExpandListingId);
    }
  }, [autoExpandListingId]);

  const toggleExpanded = (listingId: string) => {
    if (expandedListingId === listingId) {
      setExpandedListingId(null);
      return;
    }
    setExpandedListingId(listingId);
  };

  const openApplyModal = (listing: BusinessListingMarketplaceItemResponse) => {
    if (onBeforeApply && !onBeforeApply(listing)) {
      return;
    }
    setApplyModalListing(listing);
  };

  const handleApply = async (listingId: string, payload: BusinessListingApplicationRequest) => {
    setApplyingListingId(listingId);
    setFeedbackByListingId((current) => {
      const next = { ...current };
      delete next[listingId];
      return next;
    });

    try {
      await onApply(listingId, payload);
      setAppliedListingIds((current) => ({ ...current, [listingId]: true }));
      setFeedbackByListingId((current) => ({
        ...current,
        [listingId]: { tone: "success", message: "Application submitted." },
      }));
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0 ? error.message : "Unable to submit application.";
      setFeedbackByListingId((current) => ({
        ...current,
        [listingId]: { tone: "error", message },
      }));
      throw error;
    } finally {
      setApplyingListingId(null);
    }
  };

  const submitApplyModal = async (payload: BusinessListingApplicationRequest) => {
    if (!applyModalListing) {
      return;
    }
    await handleApply(applyModalListing.id, payload);
    setApplyModalListing(null);
  };

  const showNoResults = !isLoading && listings.length === 0;
  const loadingRows = Array.from({ length: 5 });
  const mobileDetailListing = mobileDetailListingId ? listings.find((listing) => listing.id === mobileDetailListingId) ?? null : null;

  return (
    <section className="mx-[calc(50%_-_50vw)] mt-1 w-screen px-2 sm:mx-0 sm:mt-5 sm:w-auto sm:px-0 md:mt-6">
      {showFilters ? (
        <div className="mb-5 rounded-2xl border border-myteengig-border bg-gradient-to-r from-white via-myteengig-background to-white p-4 shadow-sm sm:p-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-12 lg:items-end">
            <div className="col-span-2 min-w-0 sm:col-span-2 lg:col-span-12">
              <label htmlFor="business-listing-filter-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Search
              </label>
              <input
                id="business-listing-filter-search"
                type="text"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Title, business, category, city, state, zip, remote"
                className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
              />
            </div>
            <div className="min-w-0 lg:col-span-6">
              <label htmlFor="business-listing-filter-sort" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Pay
              </label>
              <select
                id="business-listing-filter-sort"
                value={paySort}
                onChange={(event) => onPaySortChange(event.target.value as PaySortOption)}
                className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
              >
                <option value="DEFAULT">Any Pay</option>
                <option value="PAY_DESC">Highest paying</option>
                <option value="PAY_ASC">Lowest paying</option>
              </select>
            </div>
            <div className="min-w-0 lg:col-span-6">
              <label htmlFor="business-listing-filter-distance" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Distance
              </label>
              <select
                id="business-listing-filter-distance"
                value={distance}
                onChange={(event) => onDistanceChange(event.target.value as DistanceFilter)}
                className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
              >
                <option value="ANY">Any distance</option>
                <option value="1">Within 1 mile</option>
                <option value="5">Within 5 miles</option>
                <option value="10">Within 10 miles</option>
                <option value="25">Within 25 miles</option>
              </select>
            </div>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <>
          <div className="grid gap-2 lg:hidden">
            {loadingRows.map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm"
              >
                <div className="grid animate-pulse grid-cols-[minmax(0,1fr)_7rem] gap-x-1 gap-y-2">
                  <div className="col-span-2 h-4 w-3/4 rounded-full bg-myteengig-background" />
                  <div className="h-3 w-full rounded-full bg-myteengig-background" />
                  <div className="ml-auto h-4 w-20 rounded-full bg-myteengig-background" />
                  <div className="h-3 w-1/2 rounded-full bg-myteengig-background" />
                  <div className="ml-auto h-3 w-14 rounded-full bg-myteengig-background" />
                </div>
              </div>
            ))}
          </div>

          <TableContainer className="hidden lg:block">
            <table className="min-w-full divide-y divide-myteengig-border">
              <TableHeader columns={columns} />
              <tbody className="divide-y divide-myteengig-border">
                {loadingRows.map((_, index) => (
                  <tr key={index}>
                    <td className="px-4 py-5 text-sm text-myteengig-text-muted" colSpan={columns.length}>
                      Loading business listings...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
        </>
      ) : null}

      {showNoResults ? (
        <>
          <div className="rounded-[1.5rem] border border-myteengig-border bg-white px-5 py-6 text-sm text-myteengig-text-muted shadow-sm lg:hidden">
            {emptyMessage}
          </div>

          <TableContainer className="hidden lg:block">
            <table className="min-w-full divide-y divide-myteengig-border">
              <TableHeader columns={columns} />
              <tbody className="divide-y divide-myteengig-border">
                <tr>
                  <td className="px-4 py-6 text-sm text-myteengig-text-muted" colSpan={columns.length}>
                    {emptyMessage}
                  </td>
                </tr>
              </tbody>
            </table>
          </TableContainer>
        </>
      ) : null}

      {!isLoading && !showNoResults ? (
        <ResponsiveDataList
          items={listings}
          columns={columns}
          getItemKey={(listing) => listing.id}
          mobileClassName="gap-2"
          renderMobileCard={(listing) => {
            const hasApplied = listing.hasApplied || Boolean(appliedListingIds[listing.id]);
            const compensationLabel = formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType);
            const durationLabel = formatBusinessListingDuration(listing.estimatedDurationMinutes) ?? formatBusinessListingJobType(listing.jobType);
            const locationLabel = formatCityStateZip(listing);
            const distanceLabel = formatDistanceLabel({
              distanceMiles: listing.distanceMiles,
              zip: listing.zip,
              comparisonZip: currentUserZip,
            }) ?? listing.businessName ?? "Business";

            return (
              <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <button
                  type="button"
                  className="grid w-full grid-cols-[minmax(0,1fr)_7rem] gap-x-1 gap-y-0.5 px-2 py-3 text-left transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-myteengig-primary"
                  onClick={() => setMobileDetailListingId(listing.id)}
                  aria-label={`View details for ${listing.title}`}
                >
                  <span className="col-span-2 block truncate text-base font-semibold leading-5 text-myteengig-text-primary">
                    {listing.title}
                  </span>
                  <span className="min-w-0 text-[0.82rem] leading-4 text-myteengig-text-muted">
                    {locationLabel}
                  </span>
                  <span className="min-w-0 truncate text-right text-[0.72rem] font-bold leading-3 text-emerald-700">
                    {compensationLabel}
                  </span>
                  <span className="min-w-0 text-[0.78rem] font-medium leading-4 text-myteengig-text-muted">
                    {distanceLabel}
                  </span>
                  <span className="min-w-0 truncate text-right text-[0.78rem] font-semibold leading-4 text-myteengig-text-muted">
                    {durationLabel}
                  </span>
                  {hasApplied ? (
                    <span className="col-span-2 mt-1 text-[0.72rem] font-semibold uppercase tracking-wide text-myteengig-primary">
                      Applied
                    </span>
                  ) : null}
                </button>
              </article>
            );
          }}
          renderDesktopRows={(listing) => {
            const hasApplied = listing.hasApplied || Boolean(appliedListingIds[listing.id]);
            const isOwnListing = Boolean(currentUserId && listing.posterUserId === currentUserId);
            const feedback = feedbackByListingId[listing.id] ?? null;
            const isExpanded = expandedListingId === listing.id;
            const isApplying = applyingListingId === listing.id;

            return (
              <Fragment key={listing.id}>
                <tr className="cursor-pointer hover:bg-myteengig-background" onClick={() => toggleExpanded(listing.id)}>
                  <td className="px-4 py-3 text-center text-sm font-medium text-myteengig-text-primary">
                    {listing.title}
                    {listing.communityServiceHoursEligible ? <span className="ml-2 text-xs font-semibold text-myteengig-primary">Service hours</span> : null}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-myteengig-text-primary">{listing.businessName || "Business"}</td>
                  <td className="px-4 py-3 text-center text-sm text-myteengig-text-primary">{formatGigListLocation(listing, currentUserZip)}</td>
                  <td className="px-4 py-3 text-center text-sm text-myteengig-text-primary">
                    {formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr>
                    <td className="px-4 py-3" colSpan={columns.length}>
                      <BusinessListingDetailGrid
                        listing={listing}
                        canApplyAsTeen={canApplyAsTeen}
                        isOwnListing={isOwnListing}
                        isApplying={isApplying}
                        hasApplied={hasApplied}
                        feedback={feedback}
                        onApply={() => openApplyModal(listing)}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          }}
        />
      ) : null}

      <Pagination
        page={page}
        pageSize={pageSize}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        resultsCount={resultsCount}
        onPageChange={onPageChange}
      />

      <Modal
        isOpen={mobileDetailListing !== null}
        onClose={() => setMobileDetailListingId(null)}
        title={mobileDetailListing?.title ?? "Business Listing Details"}
        panelClassName="max-w-3xl"
      >
        {mobileDetailListing ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-myteengig-border bg-myteengig-background/60 px-4 py-3 text-left">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                {mobileDetailListing.businessName || "Business"}
              </p>
              <p className="mt-1 text-sm font-medium text-myteengig-text-primary">
                {formatGigListLocation(mobileDetailListing, currentUserZip)}
              </p>
              <p className="mt-1 text-sm text-myteengig-text-muted">
                {formatBusinessListingJobType(mobileDetailListing.jobType)}
              </p>
            </div>

            <BusinessListingDetailGrid
              listing={mobileDetailListing}
              canApplyAsTeen={canApplyAsTeen}
              isOwnListing={Boolean(currentUserId && mobileDetailListing.posterUserId === currentUserId)}
              isApplying={applyingListingId === mobileDetailListing.id}
              hasApplied={mobileDetailListing.hasApplied || Boolean(appliedListingIds[mobileDetailListing.id])}
              feedback={feedbackByListingId[mobileDetailListing.id] ?? null}
              onApply={() => openApplyModal(mobileDetailListing)}
            />
          </div>
        ) : null}
      </Modal>
      <BusinessListingApplyModal
        isOpen={applyModalListing !== null}
        listing={applyModalListing}
        isSubmitting={applyModalListing !== null && applyingListingId === applyModalListing.id}
        onClose={() => setApplyModalListing(null)}
        onSubmit={submitApplyModal}
      />
    </section>
  );
};

export default BusinessListingsMarketplaceTable;
